class BabyDueFull extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';

    this.form = this.querySelector('[data-due-form]');
    this.result = this.querySelector('[data-due-result]');
    this.error = this.querySelector('[data-due-error]');
    if (!this.form || !this.result) return;

    this.form.querySelectorAll('[name="method"]').forEach((input) => {
      input.addEventListener('change', () => this.updateMethod());
    });
    this.form.addEventListener('submit', (event) => this.calculate(event));
    this.querySelector('[data-due-recalculate]')?.addEventListener('click', () => this.recalculate());
    this.updateMethod();
  }

  parseDate(value) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return null;
    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day ? date : null;
  }

  addDays(date, days) {
    const next = new Date(date.getTime());
    next.setUTCDate(next.getUTCDate() + days);
    return next;
  }

  daysBetween(start, end) {
    return Math.floor((end.getTime() - start.getTime()) / 86400000);
  }

  formatDate(date, short = false) {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: 'UTC',
      month: short ? 'short' : 'long',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  }

  updateMethod() {
    const method = this.form.elements.method.value;
    this.querySelectorAll('[data-due-panel]').forEach((panel) => {
      const active = panel.dataset.duePanel === method;
      panel.hidden = !active;
      panel.querySelectorAll('input, select').forEach((control) => {
        control.disabled = !active;
      });
    });
    this.hideError();
  }

  showError(message) {
    this.error.textContent = message;
    this.error.hidden = false;
  }

  hideError() {
    this.error.hidden = true;
    this.error.textContent = '';
  }

  getZodiac(date) {
    const month = date.getUTCMonth() + 1;
    const day = date.getUTCDate();
    const signs = [
      [1, 20, 'Capricorn', 'Aquarius'], [2, 19, 'Aquarius', 'Pisces'],
      [3, 21, 'Pisces', 'Aries'], [4, 20, 'Aries', 'Taurus'],
      [5, 21, 'Taurus', 'Gemini'], [6, 21, 'Gemini', 'Cancer'],
      [7, 23, 'Cancer', 'Leo'], [8, 23, 'Leo', 'Virgo'],
      [9, 23, 'Virgo', 'Libra'], [10, 23, 'Libra', 'Scorpio'],
      [11, 22, 'Scorpio', 'Sagittarius'], [12, 22, 'Sagittarius', 'Capricorn'],
    ];
    const sign = signs[month - 1];
    return day < sign[1] ? sign[2] : sign[3];
  }

  calculate(event) {
    event.preventDefault();
    this.hideError();
    if (!this.form.reportValidity()) return;

    const method = this.form.elements.method.value;
    let pregnancyStart;
    let conceptionDate;
    let dueDate;

    if (method === 'lmp') {
      const lmpDate = this.parseDate(this.form.elements.lmpDate.value);
      const cycleLength = Number(this.form.elements.cycleLength.value);
      if (!lmpDate || cycleLength < 21 || cycleLength > 45) {
        this.showError('Enter a valid last-period date and a cycle length from 21 to 45 days.');
        return;
      }
      pregnancyStart = lmpDate;
      conceptionDate = this.addDays(lmpDate, cycleLength - 14);
      dueDate = this.addDays(lmpDate, 280 + cycleLength - 28);
    } else if (method === 'conception') {
      conceptionDate = this.parseDate(this.form.elements.conceptionDate.value);
      if (!conceptionDate) {
        this.showError('Enter a valid conception date.');
        return;
      }
      pregnancyStart = this.addDays(conceptionDate, -14);
      dueDate = this.addDays(conceptionDate, 266);
    } else {
      const ultrasoundDate = this.parseDate(this.form.elements.ultrasoundDate.value);
      const weeks = Number(this.form.elements.gestationalWeeks.value);
      const days = Number(this.form.elements.gestationalDays.value);
      if (!ultrasoundDate || !Number.isInteger(weeks) || weeks < 0 || weeks > 42 || !Number.isInteger(days) || days < 0 || days > 6) {
        this.showError('Enter a valid ultrasound date, gestational week and extra days.');
        return;
      }
      pregnancyStart = this.addDays(ultrasoundDate, -(weeks * 7 + days));
      conceptionDate = this.addDays(pregnancyStart, 14);
      dueDate = this.addDays(pregnancyStart, 280);
    }

    const today = new Date();
    const todayUtc = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
    const gestationalDays = this.daysBetween(pregnancyStart, todayUtc);
    const progress = Math.min(1, Math.max(0, gestationalDays / 280));
    const completedWeeks = Math.min(40, Math.max(0, Math.floor(gestationalDays / 7)));
    let ageLabel = 'Pregnancy has not started yet';
    if (gestationalDays >= 0 && gestationalDays <= 280) {
      ageLabel = `${completedWeeks} weeks, ${gestationalDays % 7} days`;
    } else if (gestationalDays > 280) {
      ageLabel = `${gestationalDays - 280} days past due date`;
    }

    this.querySelector('[data-due-date]').textContent = this.formatDate(dueDate);
    this.querySelector('[data-due-zodiac]').textContent = `${this.getZodiac(dueDate)} baby`;
    this.querySelector('[data-conception-date]').textContent = this.formatDate(conceptionDate, true);
    this.querySelector('[data-pregnancy-age]').textContent = ageLabel;
    this.querySelector('[data-full-term]').textContent = this.formatDate(this.addDays(dueDate, -21), true);
    this.querySelector('[data-due-week]').textContent = String(completedWeeks);
    this.querySelector('[data-due-wheel]').style.setProperty('--wheel-angle', `${progress * 360}deg`);
    this.querySelector('[data-trimester-one]').textContent = `${this.formatDate(pregnancyStart, true)} - ${this.formatDate(this.addDays(pregnancyStart, 97), true)}`;
    this.querySelector('[data-trimester-two]').textContent = `${this.formatDate(this.addDays(pregnancyStart, 98), true)} - ${this.formatDate(this.addDays(pregnancyStart, 195), true)}`;
    this.querySelector('[data-trimester-three]').textContent = `${this.formatDate(this.addDays(pregnancyStart, 196), true)} - ${this.formatDate(dueDate, true)}`;
    this.result.hidden = false;
    this.result.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'nearest' });
  }

  recalculate() {
    this.result.hidden = true;
    this.form.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
    const activePanel = this.querySelector(`[data-due-panel="${this.form.elements.method.value}"]`);
    activePanel?.querySelector('input, select')?.focus({ preventScroll: true });
  }
}

if (!customElements.get('baby-due-full')) {
  customElements.define('baby-due-full', BabyDueFull);
}
